// Variant-specific content for LP1 (sales) and LP2 (call-center)

export type Variant = 'sales' | 'call-center';

export interface HeroContent {
  urgencyBadge: string;
  headline: string;
  headlineAccent: string;
  subheadline: string;
  description: string;
  painPoints: { text: string }[];
  ctaPrimary: string;
  ctaSecondary: string;
  trustSignals: string;
  stats: { value: string; label: string }[];
}

export interface StatItem {
  value: string;
  label: string;
  desc: string;
}

export interface ProblemContent {
  headline: string;
  subheadline: string;
  problems: string[];
  solutions: string[];
  problemResult: string;
  solutionResult: string;
  bottomLine: string;
  bottomLineBold: string;
}

export interface StepItem {
  title: string;
  description: string;
  time: string;
}

export interface HowItWorksContent {
  badge: string;
  headline: string;
  subheadline: string;
  steps: StepItem[];
  bottomTitle: string;
  bottomDescription: string;
  bottomNote: string;
}

export interface FeatureItem {
  title: string;
  description: string;
}

export interface FeaturesContent {
  headline: string;
  subheadline: string;
  features: FeatureItem[];
}

export interface TestimonialItem {
  content: string;
  author: string;
  role: string;
  result: string;
  image: string;
}

export interface CTAContent {
  urgencyBadge: string;
  headline: string;
  subheadline: string;
  description: string;
  ctaText: string;
  trustSignals: string;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface VariantContent {
  brandLabel: string;
  hero: HeroContent;
  stats: StatItem[];
  problem: ProblemContent;
  howItWorks: HowItWorksContent;
  features: FeaturesContent;
  testimonials: TestimonialItem[];
  cta: CTAContent;
  faq: FAQItem[];
  modalTitle: string;
  modalDescription: string;
}

// ============================================================
// LP1: SALES — Oddzwanianie do leadów z reklam FB
// Matches Ad 2: "Nie oddzwaniasz do leadów w 5 minut? Tracisz sprzedaż."
// ============================================================
export const salesContent: VariantContent = {
  brandLabel: 'AI Cold Caller',

  hero: {
    urgencyBadge: 'Wdrażamy max 5 firm w marcu — zostały 2 miejsca',
    headline: 'Twoje leady stygną.',
    headlineAccent: 'AI oddzwoni w 2 minuty.',
    subheadline: '78% szans na sprzedaż tracimy w pierwszych 5 minutach od zapisu leada. Twój handlowiec oddzwoni jutro. AI oddzwoni teraz.',
    description: 'Wdrażamy system AI, który dzwoni do leadów w ciągu 2 minut od zapisu, kwalifikuje ich budżet i gotowość, i umawia spotkania wyłącznie z decyzyjnymi osobami.',
    painPoints: [
      { text: 'Oddzwania w 2 min od zapisu' },
      { text: 'Kwalifikuje budżet i decyzyjność' },
      { text: 'Umawia tylko ciepłe leady' },
    ],
    ctaPrimary: 'Umów bezpłatną kwalifikację 30 min →',
    ctaSecondary: 'Zobacz jak to działa',
    trustSignals: '✓ Dla firm generujących min. 20 000 zł/mies. · ✓ Wdrożenie 5–7 dni · ✓ Bez długoterminowej umowy',
    stats: [
      { value: '2 min', label: 'Czas do pierwszego telefonu' },
      { value: '78%', label: 'Leadów traconych przez opóźnienie' },
      { value: '3x', label: 'Więcej umówionych spotkań' },
    ],
  },

  stats: [
    {
      value: '78%',
      label: 'Leadów tracimy przez opóźnienie',
      desc: 'Harvard: firmy oddzwaniające w 5 min mają 78× większą szansę na konwersję vs oddzwaniające po godzinie.',
    },
    {
      value: '2 min',
      label: 'Czas do pierwszego telefonu AI',
      desc: 'System AI dzwoni do leada automatycznie — bez czekania na wolnego handlowca, 24/7.',
    },
    {
      value: '3×',
      label: 'Więcej umówionych spotkań',
      desc: 'Firmy korzystające z natychmiastowego follow-up umawiają średnio 3× więcej spotkań niż przy manualnym procesie.',
    },
  ],

  problem: {
    headline: 'Problem nie jest w reklamach. Problem jest w czasie.',
    subheadline: 'Twoje leady wchodzą. Ale między zapisem a pierwszym telefonem mija za dużo czasu. A lead z każdą minutą staje się zimniejszy.',
    problems: [
      'Lead zapisuje się z reklamy o 14:37',
      'Handlowiec kończy aktualną rozmowę o 15:10',
      'Sprawdza CRM, widzi lead — dzwoni o 15:45',
      'Lead już rozmawiał z konkurencją. Nie odbiera.',
    ],
    solutions: [
      'Lead zapisuje się z reklamy o 14:37',
      'AI dzwoni automatycznie o 14:39 — 2 minuty później',
      'Przeprowadza kwalifikację: budżet, termin, decyzyjność',
      'Ustawia spotkanie z handlowcem. Lead jest ciepły.',
    ],
    problemResult: 'Wynik: Lead zimny. Sprzedaż do konkurencji.',
    solutionResult: 'Wynik: Handlowiec rozmawia tylko z ciepłymi, zakwalifikowanymi leadami.',
    bottomLine: 'Twój handlowiec nie jest zły. Po prostu ludzki mózg nie może oddzwonić w 2 minuty do każdego leada z reklam.',
    bottomLineBold: 'AI może. I robi to non-stop.',
  },

  howItWorks: {
    badge: 'Jak to działa',
    headline: 'Od zapisu leada do umówionego spotkania — w 10 minut',
    subheadline: 'Bez angażowania handlowca. Bez czekania. Bez zimnych leadów.',
    steps: [
      {
        title: '1. Lead zapisuje się z reklamy',
        description: 'Ktoś klika Twoją reklamę i wypełnia formularz. W tej samej chwili system AI dostaje sygnał.',
        time: '0:00',
      },
      {
        title: '2. AI dzwoni w 2 minuty',
        description: 'Automatyczne połączenie wychodzące — zanim lead zdąży zamknąć Facebooka. Naturalny głos, profesjonalna rozmowa.',
        time: '0:02',
      },
      {
        title: '3. Kwalifikacja: budżet i decyzyjność',
        description: 'AI zadaje pytania według Twojego scenariusza: wolumen, budżet, termin, decyzyjność. Odsiewa turystów.',
        time: '0:05',
      },
      {
        title: '4. Spotkanie w kalendarzu',
        description: 'Zakwalifikowany lead dostaje termin spotkania z Twoim handlowcem. Reszta trafia do follow-up.',
        time: '0:10',
      },
    ],
    bottomTitle: 'Zakwalifikowanych leadów z budżetem, potrzebą i umówionym terminem.',
    bottomDescription: 'Twój handlowiec dostaje tylko:',
    bottomNote: 'Żadnego cold callingu. Żadnego sprawdzania kto jest ciepły. Żadnego "oddzwonię jutro".',
  },

  features: {
    headline: 'Co dokładnie robi AI w Twoim procesie sprzedaży?',
    subheadline: 'Nie zastępuje handlowców. Zdejmuje z nich najnudniejszą i najgorszą robotę: zimny follow-up i kwalifikację.',
    features: [
      { title: 'Oddzwanianie w 2 minuty', description: 'Natychmiast po zapisie leada — bez oczekiwania na wolnego handlowca. AI działa 24/7, także w nocy i weekendy.' },
      { title: 'Inteligentna kwalifikacja', description: 'AI pyta o budżet, termin decyzji, decyzyjność i bolączki. Rozmawia według Twojego scenariusza — nie improwizuje.' },
      { title: 'Filtr jakości leadów', description: 'Tylko zakwalifikowane osoby trafiają do Twojego kalendarza. Handlowcy nie tracą czasu na turystów.' },
      { title: 'Raporty i nagrania rozmów', description: 'Pełna transkrypcja każdej rozmowy, scoring leada i podsumowanie do CRM. Wiesz kto dzwonił i co powiedział.' },
      { title: 'Scenariusz według Twoich zasad', description: 'Ustalamy razem pytania kwalifikacyjne, ton rozmowy i kryteria. AI trzyma się skryptu — zawsze.' },
      { title: 'Wdrożenie w 5–7 dni', description: 'Konfiguracja, integracja z CRM i kalendarzem, test pilotażowy. Gotowe do pracy w ciągu tygodnia.' },
    ],
  },

  testimonials: [
    {
      content: "Po wdrożeniu AI callera czas od zapisu do pierwszego kontaktu spadł z 4 godzin do 2 minut. W pierwszym miesiącu zamknęliśmy 3 deale, które wcześniej by przepadły.",
      author: "Piotr Wiśniewski",
      role: "CEO, agencja marketingowa B2B, Wrocław",
      result: "+3 deale w pierwszym miesiącu",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=150&h=150&q=80",
    },
    {
      content: "Handlowcy przestali narzekać na jakość leadów. Bo teraz dostają tylko tych, którzy mają budżet i chcą rozmawiać. Konwersja ze spotkania do deala wzrosła o 40%.",
      author: "Katarzyna Malinowska",
      role: "Sales Director, SaaS B2B, Warszawa",
      result: "+40% konwersja ze spotkania",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=150&h=150&q=80",
    },
    {
      content: "Miałem 3 handlowców. Jeden odszedł. Dzięki AI callerowi nie zatrudniłem kolejnego — system robi za niego pierwszą kwalifikację. Oszczędzam 9 tys. miesięcznie.",
      author: "Michał Kowalczyk",
      role: "Właściciel, firma usługowa, Kraków",
      result: "9 000 zł oszczędności miesięcznie",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&h=150&q=80",
    },
  ],

  cta: {
    urgencyBadge: 'Zostały 2 miejsca na wdrożenie w marcu',
    headline: 'Ile leadów stracisz w tym tygodniu?',
    subheadline: 'Każdy lead, który czeka dłużej niż 5 minut na telefon, to 78% mniejsza szansa na sprzedaż.',
    description: 'Bezpłatna kwalifikacja 30 min — sprawdzimy razem czy AI caller ma sens w Twoim modelu biznesowym i ile leadów możesz ratować.',
    ctaText: 'Umów bezpłatną kwalifikację →',
    trustSignals: '✓ Dla firm generujących min. 20 000 zł/mies. · ✓ Bez zobowiązań · ✓ 30 minut',
  },

  faq: [
    {
      question: "Jak długo trwa wdrożenie?",
      answer: "Standardowo 5–7 dni roboczych. Obejmuje to: konfigurację AI, ustalenie scenariusza rozmów, integrację z Twoim CRM i kalendarzem, oraz test pilotażowy z prawdziwymi leadami.",
    },
    {
      question: "Czy AI brzmi naturalnie? Klienci będą wiedzieć, że to robot?",
      answer: "90% rozmówców nie rozpoznaje, że rozmawia z AI — szczególnie przy krótszych rozmowach kwalifikacyjnych. Używamy najnowszych modeli syntezy głosu (ElevenLabs) z naturalnymi pauzami i intonacją. Możesz posłuchać próbki przed decyzją.",
    },
    {
      question: "Co jeśli lead pyta o coś poza scenariuszem?",
      answer: "AI trzyma się ustalonego skryptu. Jeśli lead zadaje pytanie poza scenariuszem, system grzecznie informuje, że przekieruje do konsultanta i pozostawia notatkę w CRM. Nie improwizuje.",
    },
    {
      question: "Z jakim CRM i kalendarzem się integruje?",
      answer: "Integrujemy się z Google Calendar, Calendly, HubSpot, Pipedrive i większością popularnych CRM przez API lub Zapier/Make. Przed wdrożeniem ustalamy konkretną integrację dopasowaną do Twoich narzędzi.",
    },
    {
      question: "Ile kosztuje i czy jest umowa?",
      answer: "Wdrożenie systemu to 3 000 zł miesięcznie. Obejmuje: konfigurację AI, scenariusz rozmów, integracje i bieżącą optymalizację. Nie ma długoterminowej umowy — możesz zrezygnować z miesięcznym wypowiedzeniem.",
    },
    {
      question: "Dla jakich firm to ma sens?",
      answer: "Najlepsze wyniki osiągają firmy B2B, które generują min. 30 leadów miesięcznie z reklam lub innych kanałów, a pierwszym kontaktem jest rozmowa telefoniczna. Jeśli Twoja średnia wartość transakcji to 1 000 zł+, system zwraca się już przy kilku dodatkowych dealsach miesięcznie.",
    },
  ],

  modalTitle: 'Bezpłatna kwalifikacja 30 min',
  modalDescription: 'Sprawdzimy razem czy AI caller ma sens w Twoim procesie sprzedaży',
};

// ============================================================
// LP2: CALL CENTER — Wirtualny sprzedawca AI, 100+ telefonów dziennie
// Matches Ad 1: "Wdrażamy Twojego bliźniaka AI, który codziennie wykonuje 100+ telefonów"
// ============================================================
export const callCenterContent: VariantContent = {
  brandLabel: 'AI Call Center',

  hero: {
    urgencyBadge: 'Wdrażamy max 5 firm w marcu — zostały 2 miejsca',
    headline: 'Twój zespół sprzedaży nie potrzebuje więcej ludzi.',
    headlineAccent: 'Potrzebuje AI, który dzwoni 100+ razy dziennie.',
    subheadline: 'Twoi handlowcy tracą 70% czasu na zimne telefony i niekwalifikowane kontakty. AI przejmuje prospecting — ludzie zamykają deale.',
    description: 'Wdrażamy wirtualnego sprzedawcę AI, który codziennie wykonuje 100+ telefonów, kwalifikuje leady według Twoich kryteriów i umawia spotkania tylko z decyzyjnymi klientami.',
    painPoints: [
      { text: '100+ telefonów dziennie, bez przerwy' },
      { text: 'Kwalifikuje budżet i decyzyjność' },
      { text: 'Handlowcy = closerzy, nie prospectorzy' },
    ],
    ctaPrimary: 'Umów bezpłatną kwalifikację 30 min →',
    ctaSecondary: 'Zobacz jak to działa',
    trustSignals: '✓ Dla firm generujących min. 20 000 zł/mies. · ✓ Wdrożenie 5–7 dni · ✓ Bez długoterminowej umowy',
    stats: [
      { value: '100+', label: 'Telefonów dziennie bez przerwy' },
      { value: '70%', label: 'Czasu handlowca na prospecting' },
      { value: '3x', label: 'Więcej zamkniętych deali' },
    ],
  },

  stats: [
    {
      value: '100+',
      label: 'Telefonów dziennie',
      desc: 'AI wykonuje ponad 100 połączeń dziennie — bez przerw, urlopów i spadków motywacji. 24/7, każdego dnia.',
    },
    {
      value: '70%',
      label: 'Czasu handlowca marnowanego na prospecting',
      desc: 'Przeciętny handlowiec B2B spędza 70% czasu na szukaniu kontaktów i zimnych telefonach. AI przejmuje tę robotę.',
    },
    {
      value: '3×',
      label: 'Więcej zamkniętych deali',
      desc: 'Kiedy handlowcy rozmawiają TYLKO z zakwalifikowanymi leadami, ich konwersja rośnie 3-krotnie.',
    },
  ],

  problem: {
    headline: 'Problem nie jest w handlowcach. Problem jest w procesie.',
    subheadline: 'Twoi ludzie są dobrzy. Ale tracą większość dnia na robotę, którą AI zrobi szybciej, taniej i bez narzekania.',
    problems: [
      'Handlowiec przychodzi rano, otwiera bazę kontaktów',
      'Dzwoni 30-50 razy, większość nie odbiera',
      'Z 10 rozmów — 2 mają budżet, reszta to "turysci"',
      'Na koniec dnia: 1-2 realne szanse. Reszta to stracony czas.',
    ],
    solutions: [
      'AI dzwoni do 100+ kontaktów dziennie automatycznie',
      'Prowadzi kwalifikację: budżet, termin, decyzyjność',
      'Odsiewa przypadkowe kontakty — handlowiec ich nie widzi',
      'Handlowiec dostaje 5-10 ciepłych, zakwalifikowanych spotkań dziennie.',
    ],
    problemResult: 'Wynik: Handlowiec wypalony, 2 szanse dziennie, reszta zmarnowana.',
    solutionResult: 'Wynik: Handlowiec = closer. Rozmawia tylko z decyzyjnymi klientami.',
    bottomLine: 'Twoi handlowcy nie są źli w sprzedaży. Są źli w prospectingu — bo to nie ich robota.',
    bottomLineBold: 'AI przejmuje prospecting. Ludzie zamykają deale.',
  },

  howItWorks: {
    badge: 'Jak to działa',
    headline: 'Od bazy kontaktów do umówionego spotkania — automatycznie',
    subheadline: 'AI dzwoni, kwalifikuje i umawia. Twój zespół tylko zamyka.',
    steps: [
      {
        title: '1. Wgrywasz bazę kontaktów',
        description: 'Importujesz listę kontaktów z CRM, excela lub innego źródła. AI dostaje numery i scenariusz rozmowy.',
        time: 'Start',
      },
      {
        title: '2. AI dzwoni 100+ razy dziennie',
        description: 'Automatyczne połączenia wychodzące — naturalny głos, profesjonalna rozmowa. Bez przerw na kawę czy weekendy.',
        time: '24/7',
      },
      {
        title: '3. Kwalifikacja według Twoich kryteriów',
        description: 'AI zadaje pytania: budżet, decyzyjność, potrzeba, termin. Scoring leada w czasie rzeczywistym. Turysci odsiani.',
        time: 'Auto',
      },
      {
        title: '4. Spotkanie w kalendarzu handlowca',
        description: 'Zakwalifikowany kontakt dostaje termin spotkania. Handlowiec otwiera kalendarz — ma 5-10 ciepłych rozmów dziennie.',
        time: 'Done',
      },
    ],
    bottomTitle: 'Tylko spotkań z klientami, którzy mają budżet, potrzebę i chcą podjąć decyzję.',
    bottomDescription: 'Twój handlowiec dostaje dziennie:',
    bottomNote: 'Żadnego cold callingu. Żadnego przebijania się przez sekretarki. Żadnego "wyślij mi ofertę mailem".',
  },

  features: {
    headline: 'Co robi Twój wirtualny sprzedawca AI?',
    subheadline: 'Nie zastępuje handlowców. Przejmuje najtrudniejszą i najnudniejszą część sprzedaży: pierwszy kontakt i kwalifikację.',
    features: [
      { title: '100+ telefonów dziennie', description: 'AI dzwoni bez przerwy — rano, wieczorem, w weekendy. Każdy kontakt z Twojej bazy dostaje telefon w ustalonym harmonogramie.' },
      { title: 'Naturalny głos i rozmowa', description: '90% rozmówców nie rozpoznaje AI. Używamy ElevenLabs — naturalny głos, pauzy, intonacja. Profesjonalna rozmowa sprzedażowa.' },
      { title: 'Scoring i kwalifikacja', description: 'Każdy kontakt dostaje scoring: budżet, decyzyjność, pilność, dopasowanie. Handlowiec widzi tylko tych z najwyższym potencjałem.' },
      { title: 'Transkrypcje i raporty', description: 'Pełna transkrypcja każdej rozmowy, automatyczny raport dzienny i tygodniowy. Wiesz dokładnie co AI mówi i jakie wyniki osiąga.' },
      { title: 'Twój scenariusz, Twoje zasady', description: 'Ustalamy razem skrypt rozmowy, pytania kwalifikacyjne i kryteria. AI nie improwizuje — trzyma się planu.' },
      { title: 'Wdrożenie w 5–7 dni', description: 'Konfiguracja, integracja z CRM i kalendarzem, test pilotażowy na 50 kontaktach. Gotowe w ciągu tygodnia.' },
    ],
  },

  testimonials: [
    {
      content: "Mieliśmy 2 handlowców na cold callach. Po wdrożeniu AI callera obaj przeszli na zamykanie deali. Wynik? O 3 deale więcej miesięcznie przy tym samym zespole.",
      author: "Tomasz Nowak",
      role: "VP Sales, firma technologiczna B2B, Poznań",
      result: "+3 deale miesięcznie, ten sam zespół",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=150&h=150&q=80",
    },
    {
      content: "AI dzwoni 120 razy dziennie. Moi ludzie nie byli w stanie zrobić więcej niż 40. I jakość spotkań jest lepsza, bo AI kwalifikuje lepiej niż junior handlowiec.",
      author: "Anna Zielińska",
      role: "Sales Manager, agencja rekrutacyjna, Warszawa",
      result: "3× więcej telefonów, lepsza jakość",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=150&h=150&q=80",
    },
    {
      content: "Oszczędzam 9 tys. miesięcznie na etacie juniora, który robił cold calle. AI robi to lepiej, szybciej i nie bierze L4 w poniedziałek.",
      author: "Marek Wiśniewski",
      role: "Właściciel, firma consultingowa, Kraków",
      result: "9 000 zł oszczędności miesięcznie",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&h=150&q=80",
    },
  ],

  cta: {
    urgencyBadge: 'Zostały 2 miejsca na wdrożenie w marcu',
    headline: 'Ile kosztuje Cię dzisiaj prospecting?',
    subheadline: 'Każdy handlowiec, który dzwoni na zimno zamiast zamykać deale, to stracone pieniądze.',
    description: 'Bezpłatna kwalifikacja 30 min — sprawdzimy ile telefonów dziennie potrzebuje Twój zespół i ile AI może przejąć.',
    ctaText: 'Umów bezpłatną kwalifikację →',
    trustSignals: '✓ Dla firm generujących min. 20 000 zł/mies. · ✓ Bez zobowiązań · ✓ 30 minut',
  },

  faq: [
    {
      question: "Ile telefonów dziennie wykonuje AI?",
      answer: "Standardowo 100-150 połączeń dziennie. Limit zależy od długości rozmowy i Twojego scenariusza. Przy krótszych kwalifikacjach (2-3 min) AI może wykonać nawet 200+ połączeń dziennie.",
    },
    {
      question: "Czy AI brzmi naturalnie? Ludzie będą wiedzieć, że to robot?",
      answer: "90% rozmówców nie rozpoznaje, że rozmawia z AI. Używamy najnowszych modeli syntezy głosu (ElevenLabs) z naturalnymi pauzami i intonacją. Możesz posłuchać próbki przed decyzją.",
    },
    {
      question: "Skąd biorę bazę kontaktów?",
      answer: "Z Twojego CRM, bazy danych, excela, LinkedIn Sales Navigator — dowolne źródło. Ważne, żebyś miał zgodę na kontakt (np. relacja biznesowa, zapytanie ofertowe, zgoda marketingowa). Pomagamy też w przygotowaniu bazy.",
    },
    {
      question: "Co jeśli ktoś nie odbiera?",
      answer: "AI automatycznie próbuje ponownie — domyślnie 3 próby w różnych porach dnia. Jeśli po 3 próbach brak kontaktu, lead trafia do follow-up mailowego lub SMS.",
    },
    {
      question: "Ile kosztuje i czy jest umowa?",
      answer: "Wdrożenie systemu to 3 000 zł miesięcznie. Obejmuje: konfigurację AI, scenariusz rozmów, integracje i bieżącą optymalizację. Nie ma długoterminowej umowy — możesz zrezygnować z miesięcznym wypowiedzeniem.",
    },
    {
      question: "Dla jakich firm to ma sens?",
      answer: "Dla firm B2B ze sprzedażą telefoniczną, które chcą skalować bez zatrudniania kolejnych handlowców. Minimum: 30+ kontaktów do obdzwonienia miesięcznie, średnia wartość transakcji 1 000 zł+.",
    },
  ],

  modalTitle: 'Bezpłatna kwalifikacja 30 min',
  modalDescription: 'Sprawdzimy czy AI Call Center ma sens w Twoim modelu sprzedaży',
};

export const getVariantContent = (variant: Variant): VariantContent => {
  return variant === 'call-center' ? callCenterContent : salesContent;
};
